import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button, DataTable, type Column } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useWhatsappQuery } from "../services/useWhatsapp";
import { CoverageNote, NoRecords, Pager, QueryState } from "./Shared";
import { muted } from "./styles";
import type { WhatsappUsageRow } from "../types/whatsapp.types";
const copy = keys.whatsapp;
export function UsagePanel({
  query,
  onPage,
  onDrill,
  onRefresh,
}: {
  query: string;
  onPage: (page: number) => void;
  onDrill: (
    row: WhatsappUsageRow,
    groupBy: "seller" | "store",
    target: "messages" | "usage",
  ) => void;
  onRefresh: () => void;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const [more, setMore] = useState(false);
  const result = useWhatsappQuery("usage", query);
  const data = result.data;
  const fields = more
    ? ([
        "attempts",
        "httpAccepted",
        "rejected",
        "sent",
        "delivered",
        "read",
        "deliveryFailed",
        "pending",
        "requestUnknown",
        "billable",
        "free",
        "billingUnknown",
      ] as const)
    : ([
        "attempts",
        "delivered",
        "read",
        "rejected",
        "deliveryFailed",
      ] as const);
  const columns = (groupBy: "seller" | "store"): Column<WhatsappUsageRow>[] => [
    {
      key: "name",
      label: t(groupBy === "seller" ? copy.seller : copy.store),
      width: "minmax(180px,1.6fr)",
      render: (row) => (
        <div className="rd-cell-stack">
          <span className="rd-item-name">
            <bdi>
              {row.unattributed
                ? t(groupBy === "seller" ? copy.unattributed : copy.unattributedStore)
                : (row.name ?? row.id)}
            </bdi>
          </span>
          {groupBy === "store" && row.sellerName ? (
            <span className="rd-cell-sub">
              <bdi>{row.sellerName}</bdi>
            </span>
          ) : null}
        </div>
      ),
    },
    ...fields.map((field) => ({
      key: field,
      label: t(field === "requestUnknown" ? copy.unknown : copy[field]),
      width: "minmax(76px,1fr)",
      align: "end" as const,
      numeric: true,
      render: (row: WhatsappUsageRow) => row[field],
    })),
    {
      key: "actions",
      label: "",
      width: groupBy === "seller" ? "minmax(220px,auto)" : "minmax(130px,auto)",
      align: "end",
      render: (row) => (
        <span style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="ghost" size="sm" onClick={() => onDrill(row, groupBy, "messages")}>
            {t(copy.viewMessages)}
          </Button>
          {groupBy === "seller" && !row.unattributed && (
            <Button variant="ghost" size="sm" onClick={() => onDrill(row, groupBy, "usage")}>
              {t(copy.viewStores)}
            </Button>
          )}
        </span>
      ),
    },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <p style={{ ...muted, flex: "1 1 320px", color: "var(--rd-text-secondary)" }}>{t(copy.usageSourceHint)}</p>
        <Button variant="secondary" size="sm" icon={RefreshCw} loading={result.isFetching} loadingLabel={t("whatsapp.refreshing")} onClick={onRefresh}>
          {t(copy.refresh)}
        </Button>
      </div>
      <p style={muted}>
        {t(copy.usageHint)} {t(copy.usageCountsHint)}
      </p>
      <QueryState loading={result.isLoading} error={result.error} retry={result.refetch} page={t(copy.usage).toLocaleLowerCase(locale)} rows={6}>
        {data && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <CoverageNote coverage={data.coverage} />
              <Button variant="ghost" size="sm" aria-expanded={more} onClick={() => setMore(!more)}>
                {t(more ? copy.fewerCounts : copy.moreCounts)}
              </Button>
            </div>
            <DataTable
              label={t(copy.usage)}
              rows={data.items}
              rowKey={(row) => `${row.id ?? "unattributed"}:${row.sellerId ?? "unknown"}`}
              minWidth={more ? 1380 : 900}
              empty={<NoRecords title={t("whatsapp.usageEmptyTitle")} text={t("whatsapp.usageEmptyText")} />}
              columns={columns(data.groupBy)}
              footer={data.total > 0 ? <Pager {...data} onChange={onPage} /> : undefined}
            />
          </>
        )}
      </QueryState>
    </div>
  );
}
